/**
 * AdminWorkspaceSettings — workspace branding + policy overrides.
 *
 * Persists to `/api/workspace-settings` (Admin-only PUT). Powers the
 * training-certificate PDF header (logo + org name) and the
 * training-renewal reminder cadence.
 *
 * Kept small and self-contained so it can be dropped into any admin
 * dashboard section without pulling extra state into Admin.jsx.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Save, PlayCircle, Sparkles, Rocket } from "lucide-react";
import { toast } from "sonner";
import OnboardingWizard from "@/components/OnboardingWizard";
import logger from "@/lib/logger";

const EMPTY = {
  org_name: "",
  logo_url: "",
  signatory_name: "",
  signatory_title: "",
  signatory_email: "",
  training_renewal_months: 12,
  training_renewal_warn_days: 30,
  device_portfolio_keywords: "",
};

export default function AdminWorkspaceSettings() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [reopenWizard, setReopenWizard] = useState(0);

  useEffect(() => {
    api.get("/workspace-settings")
      .then(({ data }) => {
        // Coalesce nullable string fields → '' so controlled <Input> doesn't
        // hit React's "value shouldn't be null" warning on a fresh workspace.
        const normalised = Object.fromEntries(
          Object.entries(data).map(([k, v]) => [k, v ?? (typeof EMPTY[k] === "number" ? EMPTY[k] : "")]),
        );
        setForm({ ...EMPTY, ...normalised });
      })
      .catch((e) => logger.warn("workspace-settings load:", e?.response?.status))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({
    ...f,
    [k]: e.target.type === "number" ? parseInt(e.target.value, 10) || 0 : e.target.value,
  }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/workspace-settings", form);
      const normalised = Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v ?? (typeof EMPTY[k] === "number" ? EMPTY[k] : "")]),
      );
      setForm({ ...EMPTY, ...normalised });
      toast.success("Workspace settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runRenewalScan = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/training/renewals/run-now", {});
      toast.success(`Renewal scan complete — ${data.sent} reminder${data.sent === 1 ? "" : "s"} sent`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renewal scan failed");
    } finally {
      setRunning(false);
    }
  };

  const runTriageBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/alerts/triage/backfill", {});
      if (!data.scheduled) {
        toast.success("All alerts already triaged");
      } else {
        toast.success(`AI triage scheduled — ${data.scheduled} alert${data.scheduled === 1 ? "" : "s"} triaging in the background. Refresh in ~1 min.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Triage backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Loading workspace settings…</div>;
  }

  return (
    <div className="mt-12" data-testid="admin-workspace-settings">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="vq-overline mb-2">Branding & Policy</div>
          <h2 className="font-display font-bold text-2xl text-slate-900">Workspace settings</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Applied to generated PDFs (training certificates today, more coming) and to the training-renewal reminder cadence. Optional — VitaQMS defaults are used when a field is blank.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          data-testid="reopen-onboarding-btn"
          onClick={() => setReopenWizard((n) => n + 1)}
          title="Re-open the setup wizard to walk through the essentials again"
        >
          <Rocket className="h-4 w-4 mr-1.5" />
          Re-open onboarding
        </Button>
      </div>

      <div className="vq-card p-6 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold">Organisation name</Label>
            <Input data-testid="ws-org-name" value={form.org_name} onChange={set("org_name")}
                   className="mt-1.5" placeholder="Acme Medical Devices Ltd" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Logo URL</Label>
            <Input data-testid="ws-logo-url" value={form.logo_url} onChange={set("logo_url")}
                   className="mt-1.5 font-mono text-xs" placeholder="https://cdn.example.com/logo.png" />
            <p className="text-[10px] font-mono text-slate-500 mt-1">
              Direct image URL (PNG/JPG). Fetched with a 4-second timeout — a slow URL falls back to no image.
            </p>
          </div>
          <div>
            <Label className="text-xs font-semibold">Signatory name</Label>
            <Input data-testid="ws-signatory-name" value={form.signatory_name} onChange={set("signatory_name")}
                   className="mt-1.5" placeholder="Dr. Jane Smith" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Signatory title</Label>
            <Input data-testid="ws-signatory-title" value={form.signatory_title} onChange={set("signatory_title")}
                   className="mt-1.5" placeholder="VP Quality" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Signatory email</Label>
            <Input data-testid="ws-signatory-email" value={form.signatory_email} onChange={set("signatory_email")}
                   className="mt-1.5" placeholder="quality@company.com" type="email" />
            <p className="text-[10px] font-mono text-slate-500 mt-1">Used by Risk Register Sign &amp; Email — signed binder is delivered here.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold">Training renewal (months)</Label>
            <Input data-testid="ws-renewal-months" type="number" min={1} max={60}
                   value={form.training_renewal_months} onChange={set("training_renewal_months")}
                   className="mt-1.5" />
            <p className="text-[10px] font-mono text-slate-500 mt-1">Common: 12 for GxP, 24 for general awareness.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold">Warn before renewal (days)</Label>
            <Input data-testid="ws-warn-days" type="number" min={1} max={180}
                   value={form.training_renewal_warn_days} onChange={set("training_renewal_warn_days")}
                   className="mt-1.5" />
            <p className="text-[10px] font-mono text-slate-500 mt-1">Email reminder N days before the completion becomes overdue.</p>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-200">
          <Label className="text-xs font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[#0055FF]" />
            Device portfolio keywords
            <span className="text-[10px] font-mono uppercase tracking-wider text-[#0055FF] bg-[#0055FF]/10 border border-[#0055FF]/30 rounded px-1.5 py-0.5">AI triage</span>
          </Label>
          <Textarea
            data-testid="ws-portfolio-keywords"
            value={form.device_portfolio_keywords || ""}
            onChange={set("device_portfolio_keywords")}
            className="mt-1.5 h-24 text-sm font-mono"
            placeholder="insulin pump, SaMD Class IIa, ECG monitor, Bluetooth cardiac patch, EU MDR, FDA 510(k)"
          />
          <p className="text-[10px] font-mono text-slate-500 mt-1">
            Free-text list. Used by the Regulatory Alerts AI triage (Claude Haiku 4.5) to score how relevant each incoming alert is to your device portfolio. Leave blank for generic medical-device scoring.
          </p>
        </div>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <Button
            data-testid="ws-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Save workspace settings"}
          </Button>
          <Button
            data-testid="ws-run-renewal-scan"
            onClick={runRenewalScan}
            disabled={running}
            variant="outline"
            className="border-slate-300 font-semibold"
            title="Fire the renewal-reminder scan immediately (idempotent — no re-sends within a cycle)"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            {running ? "Running…" : "Run renewal scan now"}
          </Button>
          <Button
            data-testid="ws-run-triage-backfill"
            onClick={runTriageBackfill}
            disabled={backfilling}
            variant="outline"
            className="border-[#0055FF] text-[#0055FF] font-semibold hover:bg-[#0055FF] hover:text-white"
            title="Score AI relevance for up to 50 alerts that haven't been triaged yet — call repeatedly to clear the backlog"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {backfilling ? "Triaging…" : "Backfill AI triage"}
          </Button>
          <div className="text-xs text-slate-500 flex items-center gap-1 ml-2">
            <Building2 className="w-3.5 h-3.5" />
            Applied to next generated cert PDF
          </div>
        </div>
      </div>
      {/* Local wizard instance driven by the re-open button. DashboardLayout
          also mounts its own wizard for the auto-open flow — the two never
          collide because this one is only triggered explicitly. */}
      <OnboardingWizard triggerOpen={reopenWizard} />
    </div>
  );
}
